import { BrowserRouter } from 'react-router-dom';
import { StoreProvider, useStoreContext } from './context/StoreContext';
import { lazy, Suspense, useEffect } from 'react';
import { CustomerProvider } from './context/CustomerContext';
import { WishlistProvider } from './context/WishlistContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { Store } from 'lucide-react';

// Theme apps are lazy-loaded — only the active theme's code is downloaded
const DefaultApp = lazy(() => import('./themes/default/DefaultApp'));
const MaisonApp = lazy(() => import('./themes/maison/MaisonApp'));
const NeoBrutalistApp = lazy(() => import('./themes/neo-brutalist/NeoBrutalistApp'));
const Y2KCyberApp = lazy(() => import('./themes/y2k-cyber-retro/Y2KCyberApp'));
const HeritageVintageApp = lazy(() => import('./themes/heritage-vintage/HeritageVintageApp'));
const ExpressiveMinimalApp = lazy(() => import('./themes/expressive-minimalist/ExpressiveMinimalistApp'));
const OrganicTactileApp = lazy(() => import('./themes/organic-tactile/OrganicTactileApp'));
const Playful3DApp = lazy(() => import('./themes/playful-3d/Playful3DApp'));

// Detects if we're running inside the theme editor iframe
function useEditorMode() {
  useEffect(() => {
    const isEditorMode = window.self !== window.top;
    if (isEditorMode) {
      document.body.setAttribute('data-editor', 'true');
    }

    // Listen for viewport toggle from editor
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type === 'DILZEE_VIEWPORT') {
        document.documentElement.setAttribute('data-viewport', e.data.viewport);
        // Apply mobile body width constraint
        if (e.data.viewport === 'mobile') {
          document.body.style.maxWidth = '390px';
          document.body.style.margin = '0 auto';
          document.body.style.overflowX = 'hidden';
        } else {
          document.body.style.maxWidth = '';
          document.body.style.margin = '';
        }
      }

      // Highlight active section in storefront
      if (e.data?.type === 'DILZEE_ACTIVE_SECTION') {
        // Remove previous active
        document.querySelectorAll('[data-active="true"]').forEach(el => el.removeAttribute('data-active'));
        // Set new active
        const sectionId = e.data.sectionId;
        if (sectionId) {
          const el = document.querySelector(`[data-section-id="${sectionId}"]`);
          if (el) {
            el.setAttribute('data-active', 'true');
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
}

// Clicking a section in the storefront tells the editor which section is active
function useSectionClickReporter() {
  useEffect(() => {
    const isEditorMode = window.self !== window.top;
    if (!isEditorMode) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const section = target.closest('[data-section-id]');
      if (section) {
        const sectionId = section.getAttribute('data-section-id');
        window.parent.postMessage({ type: 'DILZEE_SECTION_CLICK', sectionId }, '*');
      }
    };

    document.addEventListener('click', handleClick, { capture: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, []);
}

function useStoreHead() {
  const { store, settings } = useStoreContext();

  useEffect(() => {
    if (!store) return;

    // Title
    document.title = settings.seo_title || store.store_name;

    // Proper implementation:
    const upsertMeta = (attrs: Record<string, string>) => {
      const selector = Object.entries(attrs).filter(([k]) => k !== 'content').map(([k, v]) => `meta[${k}="${v}"]`).join('');
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    };

    const desc = settings.seo_description || settings.tagline || `Shop at ${store.store_name}`;
    const image = settings.og_image_url || settings.logo_url || settings.banner_url || '';
    const storeUrl = store.custom_domain ? `https://${store.custom_domain}` : `https://${store.subdomain}.dilzee.com`;

    upsertMeta({ name: 'description', content: desc });
    upsertMeta({ property: 'og:title', content: settings.seo_title || store.store_name });
    upsertMeta({ property: 'og:description', content: desc });
    upsertMeta({ property: 'og:type', content: 'website' });
    upsertMeta({ property: 'og:url', content: storeUrl });
    if (image) upsertMeta({ property: 'og:image', content: image });
    upsertMeta({ name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta({ name: 'twitter:title', content: settings.seo_title || store.store_name });
    upsertMeta({ name: 'twitter:description', content: desc });
    if (image) upsertMeta({ name: 'twitter:image', content: image });

    // Favicon — use favicon_url if set, otherwise logo_url
    const faviconUrl = settings.favicon_url || settings.logo_url;
    if (faviconUrl) {
      let favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
      if (!favicon) { favicon = document.createElement('link'); favicon.rel = 'icon'; document.head.appendChild(favicon); }
      favicon.href = faviconUrl;
    }

    // Theme colour for browser chrome
    upsertMeta({ name: 'theme-color', content: settings.brand_color || '#fd3c02' });

    // Canonical
    let canonical = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = storeUrl;
  }, [store, settings]);
}

function RealStoreRouter() {
  const { store, loading, error } = useStoreContext();
  useEditorMode();
  useSectionClickReporter();
  useStoreHead();

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid #e8e8e8', borderTopColor: '#fd3c02', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '24px', background: 'white' }}>
        <div style={{ fontSize: '56px', marginBottom: '16px', color: '#6b7280' }}><Store size={56} /></div>
        <h1 style={{ fontSize: '22px', fontWeight: 800, marginBottom: '8px', color: '#1a1a2e' }}>Store Not Found</h1>
        <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: 1.6 }}>The store you're looking for doesn't exist or is temporarily unavailable.</p>
      </div>
    );
  }
  if (store?.active_theme === 'maison') {
    return <Suspense fallback={<div style={{ height: '40vh', textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>Loading...</div>}><MaisonApp /></Suspense>;
  }
  if (store?.active_theme === 'neo-brutalist') {
    return <Suspense fallback={<div style={{ height: '40vh', textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>Loading...</div>}><NeoBrutalistApp /></Suspense>;
  }
  if (store?.active_theme === 'y2k-cyber-retro') {
    return <Suspense fallback={<div style={{ height: '40vh', textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>Loading...</div>}><Y2KCyberApp /></Suspense>;
  }
  if (store?.active_theme === 'heritage-vintage') {
    return <Suspense fallback={<div style={{ height: '40vh', textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>Loading...</div>}><HeritageVintageApp /></Suspense>;
  }
  if (store?.active_theme === 'expressive-minimalist') {
    return <Suspense fallback={<div style={{ height: '40vh', textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>Loading...</div>}><ExpressiveMinimalApp /></Suspense>;
  }
  if (store?.active_theme === 'organic-tactile') {
    return <Suspense fallback={<div style={{ height: '40vh', textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>Loading...</div>}><OrganicTactileApp /></Suspense>;
  }
  if (store?.active_theme === 'playful-3d') {
    return <Suspense fallback={<div style={{ height: '40vh', textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>Loading...</div>}><Playful3DApp /></Suspense>;
  }

  return <Suspense fallback={<div style={{ height: '40vh', textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>Loading...</div>}><DefaultApp /></Suspense>;
}

import { PromotionalPopup } from './components/PromotionalPopup';
import { TrackingProvider } from './context/TrackingContext';
import SpinToWinPopup from './components/apps/SpinToWinPopup';
import ChatWidget from './components/ChatWidget';
import WhatsAppWidget from './components/WhatsAppWidget';
import GlobalDrawers from './components/Drawers';

function InnerApp() {
  const { store, subdomain } = useStoreContext();
  const hasSpinToWin = store?.active_apps?.includes('spin-to-win');

  return (
    <CurrencyProvider>
      <CustomerProvider subdomain={subdomain || ''}>
        <WishlistProvider subdomain={subdomain || ''}>
          <TrackingProvider>
            <RealStoreRouter />
            <ChatWidget />
            <WhatsAppWidget />
            <GlobalDrawers />
            <PromotionalPopup />
            {hasSpinToWin && <SpinToWinPopup />}
          </TrackingProvider>
        </WishlistProvider>
      </CustomerProvider>
    </CurrencyProvider>
  );
}

export default function App() {
  return (
    <StoreProvider>
      <BrowserRouter>
        <InnerApp />
      </BrowserRouter>
    </StoreProvider>
  );
}
